import {
  type IconSize,
  type IconSource,
  type IconWeight,
  resolveIcon
} from './icon-source'

// Public types ----------------------------------------------------------------

export type IconBaseUrl = string & { readonly __iconBaseUrl: unique symbol }

// Public functions ------------------------------------------------------------

// Reads only the public asset base supplied by the server layout.
export const readIconBaseUrl = (root: HTMLElement): IconBaseUrl => {
  const configured = root.dataset.iconBaseUrl

  if (!configured) {
    throw new Error('The page is missing data-icon-base-url')
  }

  const url = new URL(configured)

  if (
    !['http:', 'https:'].includes(url.protocol) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    /[\s'"()\\<>]/.test(configured)
  ) {
    throw new Error('Invalid public icon base URL')
  }

  // This constructor owns the URL and CSS-string validation above.
  return configured.replace(/\/+$/, '') as IconBaseUrl
}

// Converts a parsed UI icon into an asset URL; malformed names fail explicitly.
export const iconSourceUrl = (
  source: IconSource,
  baseUrl: IconBaseUrl = ICONS_BASE_URL
): string => {
  if (source.status === 'invalid') {
    throw new Error('Invalid UI icon name')
  }

  return source.status === 'resolved' ? `${baseUrl}/${source.path}` : ''
}

// Supplies the same asset path to Web Awesome and retained native masks.
export const iconUrl = (
  name: unknown,
  weight: IconWeight = 'regular',
  baseUrl: IconBaseUrl = ICONS_BASE_URL
): string => iconSourceUrl(resolveIcon(name, weight), baseUrl)

// Creates a decorative square icon, including inside another component's shadow DOM.
export const createIcon = (
  name: string,
  weight: IconWeight = 'regular',
  size: IconSize = 'sm'
): HTMLElement => {
  const source = resolveIcon(name, weight)
  const icon = document.createElement('wa-icon')

  // Validate before writing any icon attributes.
  iconSourceUrl(source)
  icon.dataset.component = '.icon'
  icon.setAttribute('library', 'phosphor')
  icon.setAttribute('canvas', 'auto')
  icon.setAttribute('name', source.status === 'resolved' ? source.name : '')
  icon.setAttribute('aria-hidden', 'true')
  icon.style.fontSize = `var(--icon-size-${size})`
  icon.style.width = '1em'
  icon.style.height = '1em'
  icon.style.minWidth = '1em'
  icon.style.minHeight = '1em'

  return icon
}

// Module setup ----------------------------------------------------------------

export const ICONS_BASE_URL = readIconBaseUrl(document.documentElement)
